/**
 * GuestMobileView Component
 *
 * Mobile-optimized guest view for HMC events. Displays events in a
 * full-screen modal-style layout with:
 * - Sticky dropdown navigation for quick event jumping
 * - YouTube thumbnails with play overlay
 * - Swipeable event cards
 * - Request tickets / standby buttons
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Ticket, Clock, MapPin, Music, Calendar, Info, Users } from 'lucide-react';
import type { HMCEvent, MusicGenre } from '../hmcEventsStore';
import { useHMCEventsStore } from '../hmcEventsStore';
import { getTicketsRemaining, getCleanEventName } from '../utils/attendanceParser';
import { parseLocalDate } from '../utils/dateUtils';
import { TicketRequestModal } from './TicketRequestModal';
import { BackupSelectionFloatingButton } from './BackupSelectionFloatingButton';
import { ArtistInfoWidget } from './ArtistInfoWidget';
import { VIPMatchupPanel } from './VIPMatchupPanel';

// Genre display configuration
const GENRE_STYLES: Record<MusicGenre, { bg: string; label: string }> = {
  rap: { bg: 'bg-amber-600', label: 'Hip-Hop' },
  rnb: { bg: 'bg-purple-600', label: 'R&B' },
  latino: { bg: 'bg-red-600', label: 'Latino' },
  rock: { bg: 'bg-slate-600', label: 'Rock' },
  pop: { bg: 'bg-pink-600', label: 'Pop' },
  country: { bg: 'bg-yellow-600', label: 'Country' },
  electronic: { bg: 'bg-cyan-600', label: 'EDM' },
  other: { bg: 'bg-dark-surface/50', label: 'Other' },
};

/**
 * Extract YouTube video ID from URL
 */
const extractYouTubeVideoId = (url: string | null): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

/**
 * YouTube thumbnail quality fallback chain
 * Not all videos have all qualities - we need to try multiple
 */
const YOUTUBE_THUMBNAIL_QUALITIES = [
  'maxresdefault.jpg',  // 1280x720 - only on HD videos
  'sddefault.jpg',      // 640x480 - most videos
  'hqdefault.jpg',      // 480x360 - most videos
  'mqdefault.jpg',      // 320x180 - all videos
  'default.jpg',        // 120x90 - all videos (guaranteed)
];

/**
 * Get YouTube thumbnail URL with specified quality
 * Uses i.ytimg.com (YouTube's image CDN) for better reliability
 */
const getYouTubeThumbnailUrl = (videoId: string, qualityIndex: number = 0): string => {
  const quality = YOUTUBE_THUMBNAIL_QUALITIES[Math.min(qualityIndex, YOUTUBE_THUMBNAIL_QUALITIES.length - 1)];
  return `https://i.ytimg.com/vi/${videoId}/${quality}`;
};

/**
 * Get the best available image URL for an event
 * Priority: event_image_url > YouTube thumbnail > null (uses placeholder)
 * Same logic as emailTemplateGenerator.ts for consistency
 */
const getEventImageUrl = (event: HMCEvent): string | null => {
  // Priority 1: Direct event image URL (from scraping)
  if (event.event_image_url) {
    return event.event_image_url;
  }

  // Priority 2: YouTube thumbnail (start with hqdefault which most videos have)
  if (event.youtube_url) {
    const videoId = extractYouTubeVideoId(event.youtube_url);
    if (videoId) {
      return getYouTubeThumbnailUrl(videoId, 2); // Start with hqdefault
    }
  }

  // Priority 3: No image available - return null (will use gradient placeholder)
  return null;
};

/**
 * Get event category label
 */
const getEventCategory = (event: HMCEvent): string => {
  const opponent = (event.opponent || '').toLowerCase();

  if (event.event_type === 'dynamo' || event.event_type === 'dash') return 'Sports';
  if (opponent.includes('rockets')) return 'Sports';
  if (event.music_genre && event.music_genre !== 'other') return 'Concert';
  if (opponent.includes('comedy') || opponent.includes('comedian')) return 'Comedy';
  if (opponent.includes('wrestling') || opponent.includes('wwe')) return 'Wrestling';
  if (opponent.includes('disney') || opponent.includes('sesame')) return 'Family';
  if (opponent.includes('rodeo')) return 'Rodeo';

  return 'Event';
};

/**
 * Check if event is a sports event (Dynamo, Dash, or Rockets)
 */
const isSportsEvent = (event: HMCEvent): boolean => {
  const opponent = (event.opponent || '').toLowerCase();
  return (
    event.event_type === 'dynamo' ||
    event.event_type === 'dash' ||
    opponent.includes('rockets')
  );
};

/**
 * Check if event is specifically a Rockets game (for custom YouTube header)
 */
const isRocketsEvent = (event: HMCEvent): boolean => {
  const opponent = (event.opponent || '').toLowerCase();
  return opponent.includes('rockets');
};

/**
 * Check if event is a concert (has music genre)
 */
const isConcertEvent = (event: HMCEvent): boolean => {
  return event.music_genre !== null && event.music_genre !== 'other';
};

/**
 * Get artist name from event for concert lookup
 */
const getArtistName = (event: HMCEvent): string | null => {
  // For concerts, the opponent field typically contains the artist/performer name
  if (isConcertEvent(event) && event.opponent) {
    // Clean up the name - remove common suffixes
    return event.opponent
      .replace(/ live$/i, '')
      .replace(/ concert$/i, '')
      .replace(/ tour$/i, '')
      .replace(/ world tour$/i, '')
      .trim();
  }
  return null;
};

/**
 * Get dynamic theme colors based on event type
 */
interface EventTheme {
  primary: string;      // Primary color class (e.g., 'orange' for Dynamo)
  bgAccent: string;     // Background accent (e.g., 'bg-orange-500/10')
  borderAccent: string; // Border accent (e.g., 'border-orange-500/30')
  textAccent: string;   // Text accent (e.g., 'text-orange-400')
  buttonBg: string;     // Button background (e.g., 'bg-orange-500')
  buttonHover: string;  // Button hover (e.g., 'hover:bg-orange-600')
  gradient: string;     // Gradient for hero section
}

const getEventTheme = (event: HMCEvent): EventTheme => {
  const opponent = (event.opponent || '').toLowerCase();

  // Dynamo - Orange theme
  if (event.event_type === 'dynamo') {
    return {
      primary: 'orange',
      bgAccent: 'bg-orange-500/10',
      borderAccent: 'border-orange-500/30',
      textAccent: 'text-orange-400',
      buttonBg: 'bg-orange-500',
      buttonHover: 'hover:bg-orange-600',
      gradient: 'from-orange-600 to-orange-800',
    };
  }

  // Dash - Sky blue theme
  if (event.event_type === 'dash') {
    return {
      primary: 'sky',
      bgAccent: 'bg-sky-500/10',
      borderAccent: 'border-sky-500/30',
      textAccent: 'text-sky-400',
      buttonBg: 'bg-sky-500',
      buttonHover: 'hover:bg-sky-600',
      gradient: 'from-sky-600 to-sky-800',
    };
  }

  // Rockets - Red theme
  if (opponent.includes('rockets') || opponent.includes('lakers') || opponent.includes('warriors')) {
    return {
      primary: 'red',
      bgAccent: 'bg-red-500/10',
      borderAccent: 'border-red-500/30',
      textAccent: 'text-red-400',
      buttonBg: 'bg-red-500',
      buttonHover: 'hover:bg-red-600',
      gradient: 'from-red-600 to-red-800',
    };
  }

  // Concert - Purple theme
  if (isConcertEvent(event)) {
    return {
      primary: 'purple',
      bgAccent: 'bg-purple-500/10',
      borderAccent: 'border-purple-500/30',
      textAccent: 'text-purple-400',
      buttonBg: 'bg-purple-500',
      buttonHover: 'hover:bg-purple-600',
      gradient: 'from-purple-600 to-purple-800',
    };
  }

  // Default - Brand theme
  return {
    primary: 'brand',
    bgAccent: 'bg-brand-500/10',
    borderAccent: 'border-brand-500/30',
    textAccent: 'text-brand-400',
    buttonBg: 'bg-brand-500',
    buttonHover: 'hover:bg-brand-600',
    gradient: 'from-brand-600 to-brand-800',
  };
};

interface YouTubeThumbnailProps {
  videoId: string;
  title: string;
}

/**
 * YouTube Thumbnail with inline playback
 * Clicking the thumbnail replaces it with an embedded iframe player
 */
const YouTubeThumbnail: React.FC<YouTubeThumbnailProps> = ({ videoId, title }) => {
  // Start with hqdefault (index 2) - most videos have this quality
  const [qualityIndex, setQualityIndex] = useState(2);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const thumbnailUrl = getYouTubeThumbnailUrl(videoId, qualityIndex);

  // Handle image load error - try next quality level
  const handleImageError = useCallback(() => {
    if (qualityIndex < YOUTUBE_THUMBNAIL_QUALITIES.length - 1) {
      // Try next lower quality
      setQualityIndex(prev => prev + 1);
    } else {
      // All qualities failed - show placeholder
      setHasError(true);
    }
  }, [qualityIndex]);

  // Handle play - switch to iframe embed
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  // If playing, show embedded iframe
  if (isPlaying) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  // Show placeholder if all thumbnail qualities failed
  if (hasError) {
    return (
      <button
        onClick={handlePlay}
        className="relative w-full aspect-video rounded-xl overflow-hidden group cursor-pointer bg-gradient-to-br from-red-600 to-red-800 touch-manipulation"
        aria-label={`Play video: ${title}`}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-dark-surface/20 rounded-full flex items-center justify-center shadow-lg group-active:scale-110 transition-transform">
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="mt-3 text-white/80 text-sm font-medium">Play Video</p>
          </div>
        </div>
        <div className="absolute bottom-3 right-3 bg-black/70 rounded px-2 py-1 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
          <span className="text-xs text-white font-medium">YouTube</span>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={handlePlay}
      className="relative w-full aspect-video rounded-xl overflow-hidden group cursor-pointer bg-black touch-manipulation"
      aria-label={`Play video: ${title}`}
    >
      <img
        src={thumbnailUrl}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-200 group-active:scale-105"
        draggable={false}
        onError={handleImageError}
      />
      <div className="absolute inset-0 bg-black/30 group-active:bg-black/40 transition-all duration-150" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-active:scale-110 transition-transform">
          <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <div className="absolute bottom-3 right-3 bg-black/70 rounded px-2 py-1 flex items-center gap-1.5">
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
        <span className="text-xs text-white font-medium">YouTube</span>
      </div>
    </button>
  );
};


interface GuestMobileViewProps {
  events: HMCEvent[];
  maxGuestTickets?: number;
  /** Controlled index (when used in split view) */
  currentIndex?: number;
  /** Callback when index changes (controlled mode) */
  onIndexChange?: (index: number) => void;
  /** Whether in split view mode (removes max-width constraint) */
  isInSplitView?: boolean;
  /** Optional filter header to render above the dropdown */
  filterHeader?: React.ReactNode;
}

export const GuestMobileView: React.FC<GuestMobileViewProps> = ({
  events,
  maxGuestTickets = 4,
  currentIndex: controlledIndex,
  onIndexChange,
  isInSplitView = false,
  filterHeader,
}) => {
  // Support both controlled and uncontrolled modes
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex !== undefined;
  const currentIndex = isControlled ? controlledIndex : internalIndex;

  const setCurrentIndex = (index: number) => {
    if (isControlled && onIndexChange) {
      onIndexChange(index);
    } else {
      setInternalIndex(index);
    }
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [ticketModalEvent, setTicketModalEvent] = useState<HMCEvent | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get backup selection state from store for floating button
  const { backupSelection, restoreBackupSelection } = useHMCEventsStore();

  // Handle restore from floating button
  const handleRestoreFromMinimized = useCallback(() => {
    if (backupSelection.primaryEventId) {
      const eventToRestore = events.find((e) => e.id === backupSelection.primaryEventId);
      if (eventToRestore) {
        restoreBackupSelection();
        setTicketModalEvent(eventToRestore);
      }
    }
  }, [backupSelection.primaryEventId, events, restoreBackupSelection]);

  const currentEvent = events[currentIndex];

  // Scroll to top when controlled index changes from parent (e.g., table row click)
  useEffect(() => {
    if (isControlled && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [controlledIndex, isControlled]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Navigate to specific event
  const goToEvent = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsDropdownOpen(false);
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Navigate prev/next
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) goToEvent(currentIndex - 1);
  }, [currentIndex, goToEvent]);

  const goToNext = useCallback(() => {
    if (currentIndex < events.length - 1) goToEvent(currentIndex + 1);
  }, [currentIndex, events.length, goToEvent]);

  if (!currentEvent) {
    return (
      <div className="min-h-screen bg-dark-bg flex flex-col">
        {/* Header with filter even when no events match */}
        <div className="sticky top-0 z-40 bg-dark-surface border-b border-dark-border/30 shadow-lg">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
                <Ticket className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-semibold">USI Events</span>
            </div>
            <span className="text-sm text-dark-muted">0 events</span>
          </div>
          {filterHeader && (
            <div className="px-4 pb-3">
              {filterHeader}
            </div>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <Calendar className="w-12 h-12 text-dark-muted mx-auto mb-3" />
            <p className="text-dark-muted">No events match your filters</p>
            <p className="text-sm text-dark-muted mt-1">Try selecting more event types above</p>
          </div>
        </div>
      </div>
    );
  }

  const eventDate = new Date(currentEvent.event_date);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = currentEvent.event_start_time || currentEvent.kickoff_time || 'TBD';
  const ticketInfo = getTicketsRemaining(
    currentEvent.special_event,
    currentEvent.opponent,
    currentEvent.event_type,
    currentEvent.venue
  );
  const displayRemaining = Math.min(ticketInfo.remaining, maxGuestTickets);
  const isSoldOut = ticketInfo.remaining === 0;
  // Don't show YouTube videos for Dynamo/Dash sporting events
  const isDynamoOrDash = currentEvent.event_type === 'dynamo' || currentEvent.event_type === 'dash' ||
    currentEvent.opponent?.toLowerCase().includes('dynamo') || currentEvent.opponent?.toLowerCase().includes('dash');
  const videoId = isDynamoOrDash ? null : extractYouTubeVideoId(currentEvent.youtube_url);
  const eventName = getCleanEventName(currentEvent.opponent) || 'Event';
  const category = getEventCategory(currentEvent);
  const theme = getEventTheme(currentEvent);

  // Debug: Log youtube_url to help diagnose issues
  console.log('[GuestMobileView] Event:', eventName, 'youtube_url:', currentEvent.youtube_url, 'videoId:', videoId, 'isDynamoOrDash:', isDynamoOrDash);

  return (
    <>
      {/* Ticket Request Modal */}
      {ticketModalEvent && (
        <TicketRequestModal
          event={ticketModalEvent}
          isOpen={true}
          onClose={() => setTicketModalEvent(null)}
          maxTickets={maxGuestTickets}
          allEvents={events}
        />
      )}

      {/* Floating button when ticket request is minimized */}
      <BackupSelectionFloatingButton
        allEvents={events}
        onRestore={handleRestoreFromMinimized}
      />

      <div className="min-h-screen bg-dark-bg flex flex-col">
        {/* Desktop max-width container - centers content on larger screens (unless in split view) */}
        <div className={`guest-mobile-content w-full flex flex-col min-h-screen ${isInSplitView ? '' : 'max-w-2xl mx-auto'}`}>
        {/* Sticky Header with Dropdown Navigation - Dynamic Theme */}
        <div className={`sticky top-0 z-40 bg-dark-surface border-b shadow-lg ${theme.borderAccent}`}>
          {/* Brand Header */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 ${theme.buttonBg} rounded-lg flex items-center justify-center`}>
                <Ticket className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-semibold">USI Events</span>
            </div>
            <span className={`text-sm ${theme.textAccent}`}>
              {currentIndex + 1} of {events.length}
            </span>
          </div>

          {/* Event Dropdown Selector - Compact single-line with prominent arrow */}
          <div className="px-4 pb-2" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-1 ${theme.bgAccent} border-2 ${theme.borderAccent} rounded-lg text-left transition-all hover:border-opacity-70`}
            >
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <Calendar className={`w-4 h-4 ${theme.textAccent} flex-shrink-0`} />
                <p className="text-white font-semibold text-sm truncate">
                  {eventName} — {eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div className={`flex items-center justify-center w-8 h-8 ${theme.bgAccent} rounded-full border-2 ${theme.borderAccent} ml-2`}>
                <ChevronDown
                  className={`w-6 h-6 ${theme.textAccent} transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  strokeWidth={3}
                />
              </div>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute left-4 right-4 mt-2 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-[60vh] overflow-y-auto z-50">
                {/* Filter buttons inside dropdown */}
                {filterHeader && (
                  <div className="px-4 py-3 border-b border-dark-border/30 sticky top-0 bg-dark-surface z-10">
                    {filterHeader}
                  </div>
                )}
                {events.map((event, index) => {
                  const evDate = parseLocalDate(event.event_date);
                  const evTickets = getTicketsRemaining(event.special_event, event.opponent, event.event_type, event.venue);
                  const evRemaining = Math.min(evTickets.remaining, maxGuestTickets);
                  const isActive = index === currentIndex;

                  return (
                    <button
                      key={event.id}
                      onClick={() => goToEvent(index)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-dark-border/30 last:border-b-0 ${
                        isActive ? theme.bgAccent : 'hover:bg-dark-border/50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isActive ? theme.textAccent : 'text-white'}`}>
                          {getCleanEventName(event.opponent) || 'Event'}
                        </p>
                        <p className="text-xs text-dark-muted">
                          {evDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? 'TBD'}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        evTickets.remaining === 0
                          ? event.event_type === 'dynamo'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-amber-500/20 text-amber-400'
                          : evRemaining <= 2
                          ? event.event_type === 'dynamo'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-amber-500/20 text-amber-400'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {evTickets.remaining === 0 ? 'Standby Available' : 'Available'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div ref={containerRef} className="flex-1 overflow-y-auto pb-32">
          <div className="p-4 space-y-4">
            {/* Event Hero Image - Priority: event_image_url > gradient placeholder */}
            {/* YouTube videos moved to bottom for better UX */}
            {currentEvent.event_image_url ? (
              /* Show event image (team logo, scraped image) on gradient background */
              <div className={`w-full aspect-video rounded-xl overflow-hidden bg-gradient-to-br ${theme.gradient} flex items-center justify-center p-8`}>
                <img
                  src={currentEvent.event_image_url}
                  alt={eventName}
                  className="max-w-full max-h-full object-contain drop-shadow-lg"
                  onError={(e) => {
                    // Hide image if it fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              /* Gradient placeholder with icon */
              <div className={`w-full aspect-video rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center`}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 bg-dark-surface/20 rounded-full flex items-center justify-center">
                    {category === 'Concert' ? (
                      <Music className="w-8 h-8 text-white" />
                    ) : (
                      <Calendar className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <p className="text-white/80 text-sm font-medium">{category}</p>
                </div>
              </div>
            )}

            {/* Event Details Card - Dynamic Theme Border */}
            <div className={`bg-dark-surface border rounded-xl overflow-hidden ${theme.borderAccent}`}>
              {/* Event Header */}
              <div className={`p-4 border-b ${theme.borderAccent}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-white leading-tight">{eventName}</h2>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 ${theme.bgAccent} ${theme.textAccent} text-xs font-medium rounded border ${theme.borderAccent}`}>
                        {category}
                      </span>
                      {currentEvent.music_genre && currentEvent.music_genre !== 'other' && (
                        <span className={`px-2 py-0.5 ${GENRE_STYLES[currentEvent.music_genre].bg} text-white text-xs font-medium rounded`}>
                          {GENRE_STYLES[currentEvent.music_genre].label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className={`px-3 py-1.5 ${theme.bgAccent} border ${theme.borderAccent} rounded-lg`}>
                      <span className={`${theme.textAccent} font-semibold text-sm`}>USI Suite</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className={`p-4 border-b ${theme.borderAccent}`}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-dark-text">
                    <Calendar className={`w-5 h-5 ${theme.textAccent}`} />
                    <span>{formattedDate}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 text-dark-text">
                  <Clock className={`w-5 h-5 ${theme.textAccent}`} />
                  <span className="text-lg font-medium text-white">{formattedTime}</span>
                </div>
              </div>

              {/* Venue */}
              {currentEvent.venue && (
                <div className={`p-4 border-b ${theme.borderAccent}`}>
                  <div className="flex items-center gap-2 text-dark-text">
                    <MapPin className={`w-5 h-5 ${theme.textAccent}`} />
                    <span>{currentEvent.venue}</span>
                  </div>
                </div>
              )}

              {/* Ticket Availability */}
              <div className="p-4">
                <div className={`p-4 rounded-xl ${
                  isSoldOut
                    ? 'bg-red-500/10 border border-red-500/30'
                    : 'bg-green-500/10 border border-green-500/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Ticket className={`w-6 h-6 ${
                        isSoldOut ? 'text-red-400' : 'text-green-400'
                      }`} />
                      <div>
                        <p className={`font-bold text-lg ${
                          isSoldOut ? 'text-red-400' : 'text-green-400'
                        }`}>
                          {isSoldOut ? 'Sold Out' : 'Tickets Available'}
                        </p>
                        <p className="text-sm text-dark-muted">
                          {isSoldOut ? 'Join waitlist for cancellations' : 'Request your tickets now'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Description - Dynamic Theme */}
              {currentEvent.event_description && (
                <div className={`p-4 border-t ${theme.borderAccent}`}>
                  <div className="flex items-start gap-3">
                    <Info className={`w-5 h-5 ${theme.textAccent} mt-0.5 flex-shrink-0`} />
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-1">About This Event</h3>
                      <p className="text-sm text-dark-text leading-relaxed">{currentEvent.event_description}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Suite Information - Dynamic Theme */}
              <div className={`p-4 border-t ${theme.borderAccent} ${theme.bgAccent}`}>
                <div className="flex items-start gap-3">
                  <Users className={`w-5 h-5 ${theme.textAccent} mt-0.5 flex-shrink-0`} />
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-1">USI Suite Experience</h3>
                    <p className="text-sm text-dark-text leading-relaxed">
                      Enjoy this event from our premium USI Suite with complimentary food, drinks, and parking.
                      Perfect for client entertainment, team celebrations, or with friends and family.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${theme.bgAccent} ${theme.textAccent} border ${theme.borderAccent}`}>
                        Premium Seating
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${theme.bgAccent} ${theme.textAccent} border ${theme.borderAccent}`}>
                        Catering Included
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${theme.bgAccent} ${theme.textAccent} border ${theme.borderAccent}`}>
                        Parking Pass
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Artist Info Widget (for concerts) */}
            {isConcertEvent(currentEvent) && getArtistName(currentEvent) && (
              <ArtistInfoWidget
                artistName={getArtistName(currentEvent)!}
                defaultCollapsed={false}
              />
            )}

            {/* VIP Matchup Intelligence Panel (for Rockets games only) */}
            {isSportsEvent(currentEvent) && (
              <VIPMatchupPanel
                eventType={currentEvent.event_type}
                opponent={currentEvent.opponent}
                eventDate={currentEvent.event_date}
              />
            )}

            {/* YouTube Video Section - Moved to bottom */}
            {videoId && (
              <div className={`bg-dark-surface border rounded-xl overflow-hidden ${theme.borderAccent}`}>
                <div className={`px-4 py-3 border-b ${theme.borderAccent}`}>
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                    {isRocketsEvent(currentEvent) ? 'Previous Matchup Highlights' : 'Watch Video'}
                  </h3>
                </div>
                <div className="p-4">
                  <YouTubeThumbnail
                    videoId={videoId}
                    title={eventName}
                  />
                </div>
              </div>
            )}

            {/* Event Link */}
            {currentEvent.event_url && (
              <a
                href={currentEvent.event_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-dark-surface border border-dark-border/30 rounded-xl text-dark-text hover:text-white transition-all duration-150"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span>View Event Details</span>
              </a>
            )}
          </div>
        </div>

        {/* Fixed Bottom Navigation - Hidden when ticket modal is open on mobile for more space */}
        {!ticketModalEvent && (
          <div className={`${isInSplitView ? 'sticky' : 'fixed'} bottom-0 ${isInSplitView ? '' : 'left-0 right-0'} bg-dark-surface border-t border-dark-border/30 p-4 z-30`}>
            <div className={`${isInSplitView ? '' : 'max-w-2xl mx-auto'} flex items-center gap-2`}>
              {/* Prev Navigation - Prominent Arrow */}
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className={`w-14 h-14 flex items-center justify-center rounded-xl border-2 transition-all ${
                  currentIndex === 0
                    ? 'border-dark-border/30 text-dark-muted cursor-not-allowed bg-dark-bg/50'
                    : `${theme.borderAccent} ${theme.textAccent} ${theme.bgAccent} hover:brightness-110 active:scale-95`
                }`}
                aria-label="Previous event"
              >
                <ChevronLeft className="w-7 h-7" strokeWidth={2.5} />
              </button>

              {/* Request Tickets Button - Dynamic Theme */}
              <button
                onClick={() => setTicketModalEvent(currentEvent)}
                className={`flex-1 py-3.5 rounded-xl font-semibold text-base transition-all duration-150 ${
                  isSoldOut
                    ? 'bg-amber-500/20 text-amber-400 border-2 border-amber-500/30'
                    : `${theme.buttonBg} ${theme.buttonHover} text-white shadow-lg`
                }`}
              >
                {isSoldOut ? 'Request Standby' : 'Request Tickets'}
              </button>

              {/* Next Navigation - Prominent Arrow */}
              <button
                onClick={goToNext}
                disabled={currentIndex === events.length - 1}
                className={`w-14 h-14 flex items-center justify-center rounded-xl border-2 transition-all ${
                  currentIndex === events.length - 1
                    ? 'border-dark-border/30 text-dark-muted cursor-not-allowed bg-dark-bg/50'
                    : `${theme.borderAccent} ${theme.textAccent} ${theme.bgAccent} hover:brightness-110 active:scale-95`
                }`}
                aria-label="Next event"
              >
                <ChevronRight className="w-7 h-7" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}
        </div>{/* Close desktop max-width container */}
      </div>
    </>
  );
};

export default GuestMobileView;
